import { Config } from 'config/envConfig';
import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTimes,
    faCalendarAlt,
    faClock,
    faUser,
    faEnvelope,
    faBuilding,
    faSpinner,
    faCheck,
    faChevronLeft,
    faChevronRight,
    faClose,
    faMailReply,
    faPhone,
    faQuestionCircle,
} from '@fortawesome/free-solid-svg-icons';
import {
    Input,
    Button,
    TextArea,
    alert,
    AlertType,
    NoInteractionView,
    Spinner,
} from '@quyva/shared-ui';
import { getUrl } from '../../components/pdfDisplay/utils';

const ScheduleDemoModal = ({ isOpen, onClose }) => {
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [availableSlots, setAvailableSlots] = useState([]);
    const [loadingSlots, setLoadingSlots] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        company: '',
        phone: '',
        message: '',
    });
    const [success, setSuccess] = useState(false);

    // Reset state when modal closes
    useEffect(() => {
        if (!isOpen) {
            setStep(1);
            setSelectedDate(null);
            setSelectedSlot(null);
            setFormData({
                name: '',
                email: '',
                company: '',
                phone: '',
                message: '',
            });
            setSuccess(false);
        }
    }, [isOpen]);

    // Fetch available slots when date is selected
    useEffect(() => {
        if (selectedDate) {
            fetchAvailableSlots(selectedDate);
        }
    }, [selectedDate]);

    const fetchAvailableSlots = async (date) => {
        setLoadingSlots(true);
        try {
            const dateStr = date.toISOString().split('T')[0];
            const response = await fetch(
                getUrl(
                    `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/api/demo/available-slots?date=${dateStr}`
                )
            );
            const data = await response.json();
            if (data.success) {
                setAvailableSlots(data.slots || []);
            } else {
                setAvailableSlots([]);
            }
        } catch (error) {
            console.error('Error fetching slots:', error);
            setAvailableSlots([]);
        } finally {
            setLoadingSlots(false);
        }
    };

    const handleSubmit = async () => {
        if (!formData.name || !formData.email) {
            alert(AlertType.ERROR, 'Please fill in required fields');
            return;
        }

        if (!selectedDate || !selectedSlot) {
            alert(AlertType.ERROR, 'Please select a date and time slot');
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(
                getUrl(`http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/api/demo/schedule`),
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        ...formData,
                        date: `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`,
                        timeSlot: selectedSlot,
                    }),
                }
            );

            const data = await response.json();

            if (data.success) {
                setSuccess(true);
                alert(
                    AlertType.SUCCESS,
                    'Demo scheduled successfully! Check your email for the invite.'
                );
            } else {
                alert(
                    AlertType.ERROR,
                    data.message || 'Failed to schedule demo'
                );
            }
        } catch (error) {
            console.error('Error scheduling demo:', error);
            alert(
                AlertType.ERROR,
                'Failed to schedule demo. Please try again.'
            );
        } finally {
            setLoading(false);
        }
    };

    const generateCalendarDays = () => {
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const startingDay = firstDay.getDay();
        const totalDays = lastDay.getDate();

        const days = [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // Empty cells for days before the first day of month
        for (let i = 0; i < startingDay; i++) {
            days.push(null);
        }

        // Days of the month
        for (let day = 1; day <= totalDays; day++) {
            const date = new Date(year, month, day);
            const isWeekend = date.getDay() === 0 || date.getDay() === 6;
            const isPast = date <= today;
            days.push({
                day,
                date,
                disabled: isWeekend || isPast,
                isToday: date.getTime() === today.getTime(),
            });
        }

        return days;
    };

    const timeSlots = [
        '09:00 AM',
        '10:00 AM',
        '11:00 AM',
        '02:00 PM',
        '03:00 PM',
        '04:00 PM',
    ];

    if (!isOpen) return null;

    return (
        <div className='fixed inset-0 z-50 flex items-center justify-center'>
            {/* Backdrop */}
            <div
                className='absolute inset-0 bg-landing-black/60 backdrop-blur-sm'
                onClick={onClose}
            />

            {/* Modal */}
            <div className='relative bg-landing-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden border'>
                {/* Header */}
                <div className='bg-gradient-to-r from-landing-slate-900 to-landing-slate-800 px-6 py-5 flex items-center justify-between'>
                    <div className='flex items-start gap-3'>
                        <div className='w-12 h-12 bg-landing-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0'>
                            <FontAwesomeIcon
                                icon={faCalendarAlt}
                                className='text-landing-blue-400 text-xl'
                            />
                        </div>
                        <div className='min-w-0 pt-1'>
                            <h2 className='text-xl font-bold leading-none text-landing-white truncate'>
                                Schedule a Demo
                            </h2>
                            <p className='mt-1 text-landing-slate-400 text-sm leading-none truncate'>
                                Book a personalized walkthrough of Quyva
                            </p>
                        </div>
                    </div>
                    <Button
                        onClick={onClose}
                        className=''
                        icon={faTimes}
                        type='danger'
                    />
                </div>

                {/* Content */}
                <div className='p-6 overflow-y-auto max-h-[calc(90vh-180px)]'>
                    {success ? (
                        <div className='text-center py-12'>
                            <div className='w-20 h-20 bg-landing-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6'>
                                <FontAwesomeIcon
                                    icon={faCheck}
                                    className='text-landing-emerald-500 text-4xl'
                                />
                            </div>
                            <h3 className='text-2xl font-bold text-landing-slate-900 mb-3'>
                                Demo Scheduled!
                            </h3>
                            <p className='text-landing-slate-600 mb-6'>
                                We've sent a Google Meet invite to your email.
                                <br />
                                Looking forward to showing you Quyva!
                            </p>
                            <Button
                                onClick={onClose}
                                className='px-6 py-3'
                                text='Close'
                                type='danger'
                            />
                        </div>
                    ) : (
                        <>
                            {/* Step Indicator */}
                            <div className='flex items-center justify-center gap-4 mb-8'>
                                <div
                                    className={`flex items-center gap-2 ${
                                        step >= 1
                                            ? 'text-landing-blue-500'
                                            : 'text-landing-slate-400'
                                    }`}
                                >
                                    <div
                                        className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                                            step >= 1
                                                ? 'bg-landing-blue-500 text-landing-white'
                                                : 'bg-landing-slate-200'
                                        }`}
                                    >
                                        1
                                    </div>
                                    <span className='text-sm font-medium hidden sm:inline'>
                                        Select Date & Time
                                    </span>
                                </div>
                                <div
                                    className={`w-12 h-0.5 ${
                                        step >= 2
                                            ? 'bg-landing-blue-500'
                                            : 'bg-landing-slate-200'
                                    }`}
                                />
                                <div
                                    className={`flex items-center gap-2 ${
                                        step >= 2
                                            ? 'text-landing-blue-500'
                                            : 'text-landing-slate-400'
                                    }`}
                                >
                                    <div
                                        className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                                            step >= 2
                                                ? 'bg-landing-blue-500 text-landing-white'
                                                : 'bg-landing-slate-200'
                                        }`}
                                    >
                                        2
                                    </div>
                                    <span className='text-sm font-medium hidden sm:inline'>
                                        Your Details
                                    </span>
                                </div>
                            </div>

                            {step === 1 && (
                                <div>
                                    {/* Calendar */}
                                    <div className='mb-6'>
                                        <div className='flex items-center justify-between mb-4'>
                                            <Button
                                                onClick={() =>
                                                    setCurrentMonth(
                                                        new Date(
                                                            currentMonth.getFullYear(),
                                                            currentMonth.getMonth() -
                                                                1
                                                        )
                                                    )
                                                }
                                                className='p-2 hover:bg-landing-slate-100 rounded-lg transition'
                                                icon={faChevronLeft}
                                                type='tertiary'
                                            />
                                            <h3 className='text-lg font-semibold text-landing-slate-600'>
                                                {currentMonth.toLocaleDateString(
                                                    'en-US',
                                                    {
                                                        month: 'long',
                                                        year: 'numeric',
                                                    }
                                                )}
                                            </h3>
                                            <Button
                                                onClick={() =>
                                                    setCurrentMonth(
                                                        new Date(
                                                            currentMonth.getFullYear(),
                                                            currentMonth.getMonth() +
                                                                1
                                                        )
                                                    )
                                                }
                                                className='p-2 hover:bg-landing-slate-100 rounded-lg transition'
                                                icon={faChevronRight}
                                                type='tertiary'
                                            />
                                        </div>

                                        <div className='grid grid-cols-7 gap-1 mb-2'>
                                            {[
                                                'Sun',
                                                'Mon',
                                                'Tue',
                                                'Wed',
                                                'Thu',
                                                'Fri',
                                                'Sat',
                                            ].map((day) => (
                                                <div
                                                    key={day}
                                                    className='text-center text-xs font-semibold text-landing-slate-600 py-2'
                                                >
                                                    {day}
                                                </div>
                                            ))}
                                        </div>

                                        <div className='grid grid-cols-7 gap-1'>
                                            {generateCalendarDays().map(
                                                (dayObj, index) => (
                                                    <Button
                                                        key={index}
                                                        disabled={
                                                            !dayObj ||
                                                            dayObj.disabled
                                                        }
                                                        onClick={() =>
                                                            dayObj &&
                                                            !dayObj.disabled &&
                                                            setSelectedDate(
                                                                dayObj.date
                                                            )
                                                        }
                                                        className={`
                                                        h-10 rounded-lg text-sm font-medium transition
                                                        ${
                                                            !dayObj
                                                                ? 'invisible'
                                                                : ''
                                                        }
                                                        ${
                                                            dayObj?.disabled
                                                                ? 'bg-landing-slate-100 text-landing-slate-400 cursor-not-allowed opacity-50'
                                                                : ''
                                                        }
                                                        ${
                                                            !dayObj?.disabled &&
                                                            !(
                                                                selectedDate &&
                                                                dayObj?.date?.getTime() ===
                                                                    selectedDate.getTime()
                                                            )
                                                                ? 'hover:bg-landing-blue-50 text-landing-slate-700'
                                                                : ''
                                                        }
                                                        ${
                                                            dayObj?.isToday
                                                                ? 'ring-2 ring-landing-blue-500 ring-offset-1'
                                                                : ''
                                                        }
                                                        ${
                                                            selectedDate &&
                                                            dayObj?.date?.getTime() ===
                                                                selectedDate.getTime()
                                                                ? 'bg-landing-blue-500 text-landing-white hover:bg-landing-blue-600 active:bg-landing-blue-700'
                                                                : ''
                                                        }
                                                    `}
                                                        text={
                                                            dayObj?.day?.toString() ||
                                                            ''
                                                        }
                                                        type='tertiary'
                                                    />
                                                )
                                            )}
                                        </div>
                                    </div>

                                    {/* Time Slots */}
                                    {selectedDate && (
                                        <div>
                                            <h4 className='text-sm font-semibold text-landing-slate-600 mb-3 flex items-center gap-2'>
                                                <FontAwesomeIcon
                                                    icon={faClock}
                                                    className='text-landing-blue-500'
                                                />
                                                Available Time Slots for{' '}
                                                {selectedDate.toLocaleDateString(
                                                    'en-US',
                                                    {
                                                        weekday: 'long',
                                                        month: 'short',
                                                        day: 'numeric',
                                                    }
                                                )}
                                            </h4>
                                            {loadingSlots ? (
                                                <div className='py-8'>
                                                    <Spinner />
                                                </div>
                                            ) : (
                                                <div className='grid grid-cols-3 sm:grid-cols-4 gap-2'>
                                                    {timeSlots.map((slot) => {
                                                        const isBooked =
                                                            availableSlots.length >
                                                                0 &&
                                                            !availableSlots.includes(
                                                                slot
                                                            );
                                                        return (
                                                            <Button
                                                                key={slot}
                                                                disabled={
                                                                    isBooked
                                                                }
                                                                onClick={() =>
                                                                    setSelectedSlot(
                                                                        slot
                                                                    )
                                                                }
                                                                className={`
                                                                    py-2 px-3 rounded-lg text-sm font-medium transition
                                                                    ${
                                                                        isBooked
                                                                            ? 'bg-landing-slate-100 text-landing-slate-400 cursor-not-allowed line-through'
                                                                            : ''
                                                                    }
                                                                    ${
                                                                        !isBooked &&
                                                                        selectedSlot !==
                                                                            slot
                                                                            ? 'border border-landing-slate-200 hover:border-landing-blue-500 hover:bg-landing-blue-50 text-landing-slate-700'
                                                                            : ''
                                                                    }
                                                                    ${
                                                                        selectedSlot ===
                                                                        slot
                                                                            ? 'bg-landing-blue-500 text-landing-white border-landing-blue-500 hover:bg-landing-blue-600 active:bg-landing-blue-700'
                                                                            : ''
                                                                    }
                                                                `}
                                                                text={slot}
                                                                type='tertiary'
                                                            />
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            {step === 2 && (
                                <div className='space-y-4'>
                                    <div className='bg-landing-blue-50 rounded-lg p-4 mb-6'>
                                        <div className='text-sm text-landing-blue-800'>
                                            <strong>Selected:</strong>{' '}
                                            {selectedDate?.toLocaleDateString(
                                                'en-US',
                                                {
                                                    weekday: 'long',
                                                    month: 'long',
                                                    day: 'numeric',
                                                    year: 'numeric',
                                                }
                                            )}{' '}
                                            at {selectedSlot}
                                        </div>
                                    </div>

                                    <div className='grid sm:grid-cols-2 gap-4'>
                                        <div>
                                            <label className='block text-sm font-medium text-landing-slate-700 mb-1'>
                                                <FontAwesomeIcon
                                                    icon={faUser}
                                                    className='mr-2 text-landing-slate-400'
                                                />
                                                Full Name{' '}
                                                <span className='text-texts-red400'>
                                                    *
                                                </span>
                                            </label>
                                            <Input
                                                type='text'
                                                value={formData.name}
                                                onChange={(e) =>
                                                    setFormData({
                                                        ...formData,
                                                        name: e.target.value,
                                                    })
                                                }
                                                className='w-full'
                                                placeholder='John Doe'
                                            />
                                        </div>
                                        <div>
                                            <label className='block text-sm font-medium text-landing-slate-700 mb-1'>
                                                <FontAwesomeIcon
                                                    icon={faEnvelope}
                                                    className='mr-2 text-landing-slate-400'
                                                />
                                                Email Address{' '}
                                                <span className='text-texts-red400'>
                                                    *
                                                </span>
                                            </label>
                                            <Input
                                                type='email'
                                                value={formData.email}
                                                onChange={(e) =>
                                                    setFormData({
                                                        ...formData,
                                                        email: e.target.value,
                                                    })
                                                }
                                                className='w-full'
                                                placeholder='john@company.com'
                                            />
                                        </div>
                                    </div>

                                    <div className='grid sm:grid-cols-2 gap-4'>
                                        <div>
                                            <label className='block text-sm font-medium text-landing-slate-700 mb-1'>
                                                <FontAwesomeIcon
                                                    icon={faBuilding}
                                                    className='mr-2 text-landing-slate-400'
                                                />
                                                Company Name
                                            </label>
                                            <Input
                                                type='text'
                                                value={formData.company}
                                                onChange={(e) =>
                                                    setFormData({
                                                        ...formData,
                                                        company: e.target.value,
                                                    })
                                                }
                                                className='w-full'
                                                placeholder='Acme Inc.'
                                            />
                                        </div>
                                        <div>
                                            <label className='block text-sm font-medium text-landing-slate-700 mb-1'>
                                                <FontAwesomeIcon
                                                    icon={faPhone}
                                                    className='mr-2 text-landing-slate-400'
                                                />
                                                Phone Number
                                            </label>
                                            <Input
                                                type='tel'
                                                value={formData.phone}
                                                onChange={(e) =>
                                                    setFormData({
                                                        ...formData,
                                                        phone: e.target.value,
                                                    })
                                                }
                                                className='w-full'
                                                placeholder='+1 (555) 000-0000'
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className='block text-sm font-medium text-landing-slate-700 mb-1'>
                                            <FontAwesomeIcon
                                                icon={faQuestionCircle}
                                                className='mr-2 text-landing-slate-400'
                                            />
                                            What would you like to learn about?
                                        </label>
                                        <TextArea
                                            value={formData.message}
                                            onChange={(e) =>
                                                setFormData({
                                                    ...formData,
                                                    message: e.target.value,
                                                })
                                            }
                                            rows={3}
                                            className='w-full'
                                            placeholder='Tell us about your use case...'
                                        />
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Footer */}
                {!success && (
                    <div className='px-6 py-4 bg-landing-slate-50 border-t border-landing-slate-200 flex justify-between'>
                        {step === 1 ? (
                            <>
                                <Button
                                    onClick={onClose}
                                    className='px-6 py-2.5'
                                    icon={faClose}
                                    text='Cancel'
                                    type='danger'
                                />
                                <Button
                                    onClick={() => setStep(2)}
                                    disabled={!selectedDate || !selectedSlot}
                                    icon={faCheck}
                                    className='px-6 py-2.5'
                                    text='Continue'
                                    type='success'
                                />
                            </>
                        ) : (
                            <>
                                <Button
                                    onClick={() => setStep(1)}
                                    className='px-6 py-2.5'
                                    icon={faMailReply}
                                    text='Back'
                                    type='warning'
                                />
                                <Button
                                    onClick={handleSubmit}
                                    disabled={
                                        loading ||
                                        !formData.name ||
                                        !formData.email
                                    }
                                    className='px-6 py-2.5'
                                    text={
                                        loading
                                            ? 'Scheduling...'
                                            : 'Schedule Demo'
                                    }
                                    type='success'
                                    icon={loading ? faSpinner : faCalendarAlt}
                                />
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ScheduleDemoModal;
